import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import {
  Menu,
  LayoutDashboard,
  Building,
  Users,
  Target,
  CalendarClock,
  MessageSquare,
  Gift,
  BarChart3,
  Settings,
  FileSpreadsheet,
  LogIn,
  LogOut,
  TrendingUp,
  Calendar,
  Network,
  Map,
  Download,
  Database,
} from 'lucide-react';

export function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuth();

  const closeNav = () => setIsOpen(false);

  const handleSignOut = async () => {
    await signOut();
    closeNav();
  };

  const navigationItems = [
    {
      to: '/dashboard',
      icon: <LayoutDashboard className="h-5 w-5" />,
      label: 'Dashboard',
      active: location.pathname === '/dashboard',
    },
    {
      to: '/organizations',
      icon: <Building className="h-5 w-5" />,
      label: 'Organizations',
      active: location.pathname.startsWith('/organizations'),
    },
    {
      to: '/organization-hierarchy',
      icon: <Network className="h-5 w-5" />,
      label: 'Org Hierarchy',
      active: location.pathname.startsWith('/organization-hierarchy'),
    },
    {
      to: '/contacts',
      icon: <Users className="h-5 w-5" />,
      label: 'Contacts',
      active: location.pathname.startsWith('/contacts'),
    },
    {
      to: '/opportunities',
      icon: <Target className="h-5 w-5" />,
      label: 'Opportunities',
      active: location.pathname.startsWith('/opportunities'),
    },
    {
      to: '/tasks',
      icon: <CalendarClock className="h-5 w-5" />,
      label: 'Tasks',
      active: location.pathname.startsWith('/tasks'),
    },
    {
      to: '/calendar',
      icon: <Calendar className="h-5 w-5" />,
      label: 'Calendar',
      active: location.pathname.startsWith('/calendar'),
    },
    {
      to: '/activities',
      icon: <MessageSquare className="h-5 w-5" />,
      label: 'Activities',
      active: location.pathname.startsWith('/activities'),
    },
    {
      to: '/communications',
      icon: <MessageSquare className="h-5 w-5" />,
      label: 'Communications',
      active: location.pathname.startsWith('/communications'),
    },
    {
      to: '/gifts',
      icon: <Gift className="h-5 w-5" />,
      label: 'Gifts',
      active: location.pathname.startsWith('/gifts'),
    },
    {
      to: '/landexpand',
      icon: <TrendingUp className="h-5 w-5" />,
      label: 'Land & Expand',
      active:
        location.pathname.startsWith('/landexpand') || location.pathname.startsWith('/land-expand'),
    },
    {
      to: '/import',
      icon: <FileSpreadsheet className="h-5 w-5" />,
      label: 'Import Data',
      active: location.pathname.startsWith('/import'),
    },
    {
      to: '/export',
      icon: <Database className="h-5 w-5" />,
      label: 'Export Data',
      active: location.pathname.startsWith('/export'),
    },
    {
      to: '/analytics',
      icon: <BarChart3 className="h-5 w-5" />,
      label: 'Analytics',
      active: location.pathname.startsWith('/analytics'),
    },
  ];

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="md:hidden"
          aria-label="Open navigation menu"
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent 
        side="left" 
        className="w-64 p-0"
        id="mobile-navigation"
        aria-label="Mobile navigation menu"
      >
        <SheetHeader className="p-6 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary" aria-hidden="true" />
            <span>Spherical SRM</span>
          </SheetTitle>
        </SheetHeader>

        <nav className="flex flex-col h-full px-2" role="navigation">
          <div className="flex-1 space-y-1" role="list">
            {navigationItems.map((item) => (
              <MobileNavItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                active={item.active}
                onClick={closeNav}
              />
            ))}
          </div>

          <div className="border-t pt-4 pb-6 space-y-1" role="list">
            <MobileNavItem
              to="/settings"
              icon={<Settings className="h-5 w-5" />}
              label="Settings"
              active={location.pathname.startsWith('/settings')}
              onClick={closeNav}
            />
            {user ? (
              <Button
                role="listitem"
                variant="ghost"
                size="sm"
                className="w-full justify-start h-9 select-none transition-colors"
                onClick={handleSignOut}
                aria-label="Sign out of your account"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
                <span className="ml-2">Sign Out</span>
              </Button>
            ) : (
              <MobileNavItem
                to="/auth"
                icon={<LogIn className="h-5 w-5" />}
                label="Sign In"
                active={location.pathname.startsWith('/auth')}
                onClick={closeNav}
              />
            )}
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

interface MobileNavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}

function MobileNavItem({ to, icon, label, active = false, onClick }: MobileNavItemProps) {
  // If there's a custom onClick, don't use Link
  if (onClick) {
    return (
      <Button
        role="listitem"
        variant={active ? 'default' : 'ghost'}
        size="sm"
        className="w-full justify-start h-9 select-none transition-colors"
        onClick={onClick}
        aria-label={label}
        aria-current={active ? 'page' : undefined}
      >
        <span aria-hidden="true">{icon}</span>
        <span className="ml-2">{label}</span>
      </Button>
    );
  }

  // For regular navigation, use asChild with Link
  return (
    <Button
      role="listitem"
      variant={active ? 'default' : 'ghost'}
      size="sm"
      className="w-full justify-start h-9 select-none transition-colors"
      asChild
      aria-current={active ? 'page' : undefined}
    >
      <Link to={to} onClick={onClick} className="flex items-center w-full" aria-label={label}>
        <span aria-hidden="true">{icon}</span>
        <span className="ml-2">{label}</span>
      </Link>
    </Button>
  );
}
